import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Eye, Flame, Loader2, Plus, Search } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Crumbs } from "@/components/layout/Crumbs";
import { Fold, FoldBody } from "@/components/layout/Fold";
import { LandingHead } from "@/components/shop/GroupCard";
import { Reveal } from "@/components/Reveal";
import api, { mediaUrl } from "@/lib/api";
import { StoreButton } from "@/components/Buttons";
import { useAuth } from "@/context/AuthContext";
import { SoldBy } from "@/components/SoldBy";
import { useMall } from "@/context/MallContext";
import { sortStoreFirst, themeOf } from "@/lib/mall";

export const KIND_TABS = [
    { key: "machine", label: "Machines" },
    { key: "supply", label: "Supplies" },
    { key: "blank", label: "Blanks" },
    { key: "apparel", label: "Apparel" },
    { key: "class", label: "Classes" },
    { key: "wanted", label: "Wanted" },
];

export const money = (n) => (n === null || n === undefined ? "Make an offer" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: 2 })}`);

/** One ad. Full color for every seller; the shop you are in gets a "This shop" tag. */
export function ListingCard({ l, to }) {
    const { store: active } = useMall();
    const mine = Boolean(active?.slug && (l.store?.slug || l.store_slug) === active.slug);
    const t = themeOf(active);
    return (
        <Link to={to || `/supplies/${l.slug}`} data-testid={`listing-card-${l.slug}`} data-mine={mine || undefined}
            className="group flex h-full flex-col border-2 border-zinc-900 bg-white transition-transform duration-200 hover:-translate-y-1">
            <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-zinc-900 bg-zinc-100">
                {l.images?.[0] ? (
                    <img src={mediaUrl(l.images[0])} alt={l.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                    <div className="flex h-full items-center justify-center text-zinc-400"><Search size={28} /></div>
                )}
                {mine && (
                    <span className="absolute left-0 top-0 px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-widest" style={{ background: t.primary, color: t.on }} data-testid={`listing-mine-${l.slug}`}>This shop</span>
                )}
                {l.featured && !mine && (
                    <span className="absolute left-0 top-0 inline-flex items-center gap-1 bg-zinc-900 px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-white"><Flame size={11} /> Featured</span>
                )}
                {l.status === "sold" && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/70 font-display text-2xl font-black uppercase tracking-widest text-red-400">Sold</span>
                )}
                <span className="absolute right-0 top-0 bg-white px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest text-zinc-900">{l.condition}</span>
            </div>
            <div className="flex flex-1 flex-col p-4">
                <p className="font-mono text-2xl font-black text-zinc-900">{money(l.price)}</p>
                <h3 className="mt-1 font-display text-base font-bold leading-tight text-zinc-900">{l.title}</h3>
                <p className="mt-2 flex-1 line-clamp-2 text-sm text-zinc-600">{l.description}</p>
                <SoldBy store={l.store || { slug: l.store_slug || "", name: l.seller_name, city: l.location, verified: l.seller_kind === "shop" }} className="mt-3" withFulfillment={false} />
                <div className="mt-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                    <span>{l.store?.fulfillment_line || l.location}</span>
                    <span className="inline-flex items-center gap-1"><Eye size={11} /> {l.views || 0}</span>
                </div>
            </div>
        </Link>
    );
}

/** CLASSIFIEDS (Phase 10): post / search first, the ads under it, the explainer folded away at the bottom. */
export default function Marketplace() {
    const [sp, setSp] = useSearchParams();
    const { user } = useAuth();
    const { store: active } = useMall();
    const kind = KIND_TABS.some((t) => t.key === sp.get("kind")) ? sp.get("kind") : sp.get("q") ? null : "machine";
    const [q, setQ] = useState(sp.get("q") || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/marketplace/listings", { params: { kind: kind || undefined, q: sp.get("q") || undefined } })
            .then(({ data }) => setData(data)).catch(() => { }).finally(() => setLoading(false));
    }, [kind, sp]);

    const counts = data?.counts || {};
    const listings = useMemo(() => sortStoreFirst(data?.listings || [], active?.slug), [data, active?.slug]);
    const theme = themeOf(active);

    const setKind = (k) => setSp({ kind: k, ...(sp.get("q") ? { q: sp.get("q") } : {}) });
    const search = (e) => { e.preventDefault(); setSp(q ? { q } : { kind: kind || "machine" }); };

    return (
        <Layout title="Classifieds">
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="classifieds-page">
                <Crumbs trail={[{ label: "Classifieds" }]} />
                <div className="mt-4">
                    <LandingHead kicker="Classifieds · machines, supplies, blanks, classes" testid="marketplace-intro"
                        title="Buy and sell print gear."
                        line="Shops post used machines, supplies, blanks, apparel and printing classes. Every ad says who is selling and where they are — you message the seller from the ad and make the deal yourselves."
                        right={(
                            <div className="flex flex-wrap gap-2">
                                <Link to={user ? "/supplies/sell" : "/login?next=/supplies/sell"} data-testid="sell-cta">
                                    <StoreButton theme={theme} data-testid="sell-cta-button" className="px-5 py-3 text-xs"><Plus size={16} /> Post an ad</StoreButton>
                                </Link>
                                {user && <Link to="/account/listings" data-testid="my-listings-link" className="inline-flex items-center border-2 border-zinc-900 px-5 py-3 text-xs font-bold uppercase tracking-wide text-zinc-900 hover:bg-zinc-900 hover:text-white">My listings</Link>}
                            </div>
                        )} />
                </div>

                <div className="mt-6 flex flex-col gap-4 border-y-2 border-zinc-900 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap gap-2" data-testid="kind-tabs">
                        {KIND_TABS.map((t) => (
                            <button key={t.key} onClick={() => setKind(t.key)} data-testid={`kind-tab-${t.key}`} aria-pressed={kind === t.key}
                                className={`border-2 px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${kind === t.key ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 text-zinc-800 hover:border-zinc-900"}`}>
                                {t.label} <span className="font-mono text-[11px] opacity-70">{counts[t.key] ?? ""}</span>
                            </button>
                        ))}
                    </div>
                    <form onSubmit={search} className="flex gap-2">
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search film, i1600, press…"
                            className="w-full border-2 border-zinc-900 bg-white px-4 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-500 focus:outline-none sm:w-72"
                            data-testid="marketplace-search-input" />
                        <button type="submit" className="flex h-11 w-11 items-center justify-center bg-zinc-900 text-white" data-testid="marketplace-search-btn" aria-label="Search"><Search size={16} /></button>
                    </form>
                </div>

                {!kind && sp.get("q") && (
                    <p className="mt-5 text-sm text-zinc-600" data-testid="search-scope-note">
                        Searching every category for “{sp.get("q")}”.{" "}
                        <button onClick={() => { setQ(""); setSp({ kind: "machine" }); }} className="font-semibold underline" data-testid="clear-search">Clear search</button>
                    </p>
                )}

                {loading ? (
                    <div className="flex min-h-[40vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
                ) : listings.length === 0 ? (
                    <div className="mt-8 border-2 border-dashed border-zinc-300 p-12 text-center" data-testid="marketplace-empty">
                        <p className="text-zinc-600">Nothing here yet. Be the first to list.</p>
                        <Link to={user ? "/supplies/sell" : "/login?next=/supplies/sell"} className="mt-4 inline-block"><StoreButton theme={theme}><Plus size={16} /> Post an ad</StoreButton></Link>
                    </div>
                ) : (
                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="listing-grid">
                        {listings.map((l, i) => (
                            <Reveal key={l.slug} delay={Math.min(i, 8) * 0.04}><ListingCard l={l} /></Reveal>
                        ))}
                    </div>
                )}
            </section>

            <Fold title="How the classifieds work" kicker="The rules" testid="classifieds-fold-how">
                <FoldBody>
                    <div className="grid gap-6 md:grid-cols-3">
                        {[
                            ["Post it", "Sign in, pick a category, add photos and a price. New ads are reviewed before they go live — usually the same day."],
                            ["Deal direct", "Buyers message you from the ad. Phone numbers stay private until you share them. The shop takes no cut."],
                            ["Mark it sold", "Sold, relisted or removed — you manage every ad from My listings in your account."],
                        ].map(([h, p]) => (
                            <div key={h} className="border-2 border-zinc-900 p-5">
                                <h3 className="font-display text-lg font-black uppercase tracking-tight">{h}</h3>
                                <p className="mt-2 text-sm text-zinc-700">{p}</p>
                            </div>
                        ))}
                    </div>
                </FoldBody>
            </Fold>
        </Layout>
    );
}
